import type { SizeOption } from '../domain/models/CustomizationOptions';

export type { SizeOption };

function normalizeCustomizationOptions(customizationOptions: unknown): Record<string, unknown> | null {
  if (!customizationOptions) return null;
  if (typeof customizationOptions === 'string') {
    try {
      const parsed = JSON.parse(customizationOptions) as unknown;
      return parsed && typeof parsed === 'object'
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  if (typeof customizationOptions === 'object') {
    return customizationOptions as Record<string, unknown>;
  }
  return null;
}

function mapSizeEntry(key: string, row: Record<string, unknown>): SizeOption {
  return {
    key: String(row.key ?? row.size ?? key),
    value: String(row.value ?? row.displaySize ?? row.size ?? key),
  };
}

function parseSizeArray(sizes: unknown[]): SizeOption[] {
  return sizes
    .map((item) => {
      if (item == null) return { key: '', value: '' };

      if (typeof item === 'number' || typeof item === 'string') {
        const label = String(item);
        return { key: label, value: label };
      }

      if (typeof item === 'object') {
        return mapSizeEntry('', item as Record<string, unknown>);
      }

      return { key: '', value: '' };
    })
    .filter((item) => item.key !== '');
}

function parseSizeObject(sizes: Record<string, unknown>): SizeOption[] {
  return Object.entries(sizes)
    .map(([key, item]) => {
      if (item == null) return { key: '', value: '' };

      if (typeof item === 'number' || typeof item === 'string') {
        const label = String(item);
        return { key: key, value: label };
      }

      if (typeof item === 'object') {
        return mapSizeEntry(key, item as Record<string, unknown>);
      }

      return { key: '', value: '' };
    })
    .filter((item) => item.key !== '');
}

export function parseSizeList(customizationOptions: unknown): SizeOption[] {
  const opts = normalizeCustomizationOptions(customizationOptions);
  if (!opts) return [];

  const sizes = opts['size'];
  if (Array.isArray(sizes)) {
    return parseSizeArray(sizes);
  }

  if (sizes && typeof sizes === 'object') {
    return parseSizeObject(sizes as Record<string, unknown>);
  }

  return [];
}

export function normalizeSizeKey(key: string | number | null | undefined): string {
  if (key == null || key === '') return '';
  const keyStr = String(key).trim();
  if (/^0+\d+$/.test(keyStr)) {
    return String(parseInt(keyStr, 10));
  }
  return keyStr;
}

export function findSizeOption(sizes: SizeOption[], key: string | number | null | undefined): SizeOption | null {
  if (key == null || key === '') return null;
  const normalizedKey = normalizeSizeKey(key);
  return sizes.find((size) => normalizeSizeKey(size.key) === normalizedKey) ?? null;
}

export function getSizeLabel(category: string): string {
  const lengthCategories = ['Chains', 'Mangalsutra Chains', 'Necklaces', 'Anklets'];
  return lengthCategories.includes(category) ? 'Length (in inches)' : 'Size';
}
